import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { entriesLoadErrorMessage } from '../../utils/entries-load-error';
import type { EntriesLoadError } from '../../utils/entries-load-error.types';
import type { EntriesLoadErrorAlertType } from './entries-load-error-alert.types';

/**
 * Presentational notice for a permission-gated entries load that failed — see
 * `entries-load-error.ts` for why 'forbidden' and 'failed' need different copy. Pure
 * inputs/outputs, no injected services: every linked/linkage Formly field that fetches entries
 * behind permission gating (`linked-items`, `linkage-items`, `linkage-tags`, `linkage-select`,
 * `select-linked-entries`) renders this same alert instead of five hand-maintained copies of it.
 *
 * `:host { display: block }` so the margin/clear utility classes each caller passes on the tag
 * itself (e.g. `class="my-4 clear"`) behave the same as they did on the `fuse-alert` directly.
 *
 * data-cy convention: `<dataCyPrefix>_load-error`, and `<dataCyPrefix>_retry-button` when
 * `showRetry` is set.
 */
@Component({
  selector: 'app-entries-load-error-alert',
  imports: [FuseAlertComponent, MatButtonModule],
  template: `
    <fuse-alert [type]="alertType()" [attr.data-cy]="dataCyPrefix() + '_load-error'">
      <div class="flex items-center justify-between gap-4">
        <span>{{ message() }}</span>
        @if (showRetry()) {
          <button
            type="button"
            class="flex flex-0 dd-secondary-button dd-button-sm"
            mat-flat-button
            [attr.data-cy]="dataCyPrefix() + '_retry-button'"
            (click)="retry.emit()">
            Opnieuw proberen
          </button>
        }
      </div>
    </fuse-alert>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntriesLoadErrorAlertComponent {
  readonly error = input.required<EntriesLoadError>();
  /**
   * Field label as configured. Formly `props` is untyped, so callers bind `props.label` as
   * `string | undefined` at runtime even without strictTemplates — `entriesLoadErrorMessage`
   * falls back to 'gekoppelde items' when it is absent, see entries-load-error.ts.
   */
  readonly label = input<string | undefined>();
  readonly dataCyPrefix = input.required<string>();
  readonly showRetry = input(false);
  /**
   * `fuse-alert` type for the 'failed' case only. 'forbidden' always renders as 'warning' — see
   * entries-load-error-alert.types.ts.
   */
  readonly failedAlertType = input<EntriesLoadErrorAlertType>('warning');

  readonly retry = output<void>();

  protected readonly message = computed(() => entriesLoadErrorMessage(this.error(), this.label()));
  protected readonly alertType = computed<EntriesLoadErrorAlertType>(() =>
    this.error() === 'forbidden' ? 'warning' : this.failedAlertType()
  );
}
